import { type MouseEvent } from 'react';
import { useRepubContext } from '@cx-web/feature-repub';

import { type Bet } from '~/components/BettingInsights/types';

import { useNonOCRepubBetslip } from './useNonOCRepubBetslip.tsx';
import { useOCRepubBetslip } from './useOCRepubBetslip.tsx';

/**
 * Single betslip interface for the OC and non-OC repub paths.
 *
 * OC drives an external `window.ocBetslip` widget (loaded async); everyone else drives the in-repo
 * `@cx-web/feature-betslip` store. Both expose the same `{ isBetInBetslip, handleClick }` surface, so
 * callers no longer branch on repub — that lives here. WS (clickout-only) has no betslip and keeps its
 * own hook. Both underlying hooks are called unconditionally to satisfy the rules of hooks; the OC hook
 * preserves its own 'not-ready-yet' polling for the async widget.
 */
export const useBetslip = (args: {
  bet: Bet;
  bookieName: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { repubIs } = useRepubContext();

  // OC drives the external widget and has no use for bookieName; pass only what it declares.
  const ocRepubBetslip = useOCRepubBetslip({ bet: args.bet, onClick: args.onClick });
  const nonOCRepubBetslip = useNonOCRepubBetslip(args);

  return repubIs.OC ? ocRepubBetslip : nonOCRepubBetslip;
};
